import { Box, type BoxProps, Flex, Text, Divider } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { type IntegrationSimple } from "lib/sanity";
import { AnimatePresence } from "framer-motion";
import { BallIcon, EmailIcon, TextStringIcon, TimeIcon, NumberIcon } from "ui/icons";
import { ChakraBox, ChakraList, ChakraListItem, ChakraText } from "ui/chakra-framer";
import { DestinationRowItem } from "./destination-row-item";
import UseCaseHeading from "./heading";
import { SourceRowItem } from "./source-row-item";
import { dividerMoveX, ballMoveX, connectorStaggerChildren, rowItemOpacity } from "./animations";

export type UseCaseIconType = "email" | "text" | "number" | "time";

export type UseCaseProps = {
  id: string;
  head: {
    source?: IntegrationSimple;
    sourceTitle: string;
    destination?: IntegrationSimple;
    destinationTitle: string;
  };
  rows: Array<{
    sourceData: string;
    destinationData: string;
    destinationIcon: UseCaseIconType;
  }>;
};

export type UseCaseAnimationProps = {
  useCases: UseCaseProps[];
  isHero?: boolean;
} & BoxProps;

function UseCaseAnimation({ isHero = false, useCases, ...props }: UseCaseAnimationProps) {
  const [activeAnimationIndex, setActiveAnimationIndex] = useState<number>(0);
  const [nextAnimationIndex, setNextAnimationIndex] = useState<number>(0);
  const [isFirstLoop, setIsFirstLoop] = useState<boolean>(true);

  useEffect(() => {
    if (useCases.length === 2) {
      setNextAnimationIndex(activeAnimationIndex === 0 ? 1 : 0);
    } else if (useCases.length > 2) {
      setNextAnimationIndex(activeAnimationIndex === useCases.length - 1 ? 0 : activeAnimationIndex + 1);
    }
  }, [activeAnimationIndex, useCases.length]);

  return (
    <Flex
      pos="relative"
      borderRadius="md"
      bgGradient="linear(to-b, grassGradientDark 0%, grassGradientLight 150%)"
      px={{ base: 4, md: 9 }}
      py={{ base: 12, md: 32 }}
      justify="center"
      align="center"
      fontSize={{ base: "9px", sm: "10px", lg: "12px" }}
      fontFamily="inter"
      h={{ base: "340px", md: "460px" }}
      w="560px"
      maxW={{ base: "100%", md: "640px" }}
      {...props}
    >
      <AnimatePresence>
        <ChakraBox
          key="animation-container"
          pos="relative"
          bg="white"
          borderRadius="md"
          w="100%"
          px={{ base: 4, md: 8 }}
          py={{ base: 6, md: 8 }}
          pb={{ base: 8, md: 10 }}
          boxShadow="md"
          initial={{
            opacity: 0,
            scale: 0,
          }}
          animate={{
            opacity: 1,
            scale: 1,
            transition: {
              duration: 0.1,
              type: "spring",
              bounce: 0.2,
            },
          }}
        >
          <UseCaseHeading
            useCases={useCases}
            isHero={isHero}
            activeAnimationIndex={activeAnimationIndex}
            isFirstLoop={isFirstLoop}
          />

          <Box key={`${useCases[activeAnimationIndex].head.sourceTitle}-${activeAnimationIndex}`} overflow="hidden">
            <ChakraList
              initial="hidden"
              animate="visible"
              // @ts-expect-error Chakra and framer conflict
              transition={{
                staggerChildren: 0.3,
                delay: isFirstLoop ? 0.8 : 0.4,
              }}
            >
              {useCases[activeAnimationIndex].rows.map((row, rowIndex) => {
                let icon = null;

                switch (row.destinationIcon) {
                  case "email": {
                    icon = EmailIcon;

                    break;
                  }

                  case "text": {
                    icon = TextStringIcon;

                    break;
                  }

                  case "number": {
                    icon = NumberIcon;

                    break;
                  }

                  case "time": {
                    icon = TimeIcon;

                    break;
                  }

                  default:
                  // Do nothing
                }

                return (
                  <ChakraListItem
                    key={`${row.sourceData}-${activeAnimationIndex}`}
                    display="flex"
                    alignItems="stretch"
                    w="100%"
                    mt={4}
                  >
                    <SourceRowItem>
                      <AnimatePresence>
                        <ChakraText
                          key={`${row.sourceData}-source-${useCases[activeAnimationIndex].id}`}
                          variants={rowItemOpacity(rowIndex)}
                          textStyle="uppercase"
                          fontFamily="mono"
                          fontWeight="normal"
                        >
                          {row.sourceData}
                        </ChakraText>
                      </AnimatePresence>
                    </SourceRowItem>

                    <ChakraBox
                      variants={connectorStaggerChildren}
                      display="flex"
                      flexDirection="column"
                      justifyContent="center"
                      flex={{ base: 0.75, md: 1 }}
                      maxW={100}
                      pos="relative"
                    >
                      <ChakraBox variants={dividerMoveX}>
                        <Divider borderBottomWidth="1.5px" borderColor="base.border" />
                      </ChakraBox>

                      <ChakraBox
                        variants={ballMoveX}
                        pos="absolute"
                        onAnimationComplete={() => {
                          // When the final row's connector animation ends, trigger the next use case animation.
                          if (rowIndex === useCases[activeAnimationIndex].rows.length - 1) {
                            setActiveAnimationIndex(nextAnimationIndex);

                            if (isFirstLoop) {
                              setIsFirstLoop(false);
                            }
                          }
                        }}
                      >
                        <BallIcon boxSize={5} />
                      </ChakraBox>
                    </ChakraBox>

                    <DestinationRowItem>
                      <AnimatePresence>
                        <ChakraBox
                          key={`${row.destinationData}-destination-${useCases[activeAnimationIndex].id}`}
                          variants={rowItemOpacity(rowIndex)}
                          display="flex"
                          alignItems="center"
                          gap={{ base: "2px", sm: 2 }}
                        >
                          {icon && <Flex as={icon} align="center" boxSize={{ base: 4, md: 5 }} />}

                          <Text fontWeight="medium">{row.destinationData}</Text>
                        </ChakraBox>
                      </AnimatePresence>
                    </DestinationRowItem>
                  </ChakraListItem>
                );
              })}
            </ChakraList>
          </Box>
        </ChakraBox>
      </AnimatePresence>
    </Flex>
  );
}

export default UseCaseAnimation;
